import React, { useState } from 'react';
import { Send, CheckCircle2, Loader2, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuoteForm = ({ variant = "default" }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    property_type: '',
    property_address: '',
    requested_date: '',
    request_details: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.service_type || !formData.property_address) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('ach_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/quotes`, formData, { headers });
      setIsSubmitted(true);
      toast.success('Quote request submitted successfully! We\'ll be in touch soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service_type: '',
        property_type: '',
        property_address: '',
        requested_date: '',
        request_details: '',
        message: ''
      });
      
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote request. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCompact = variant === "compact";

  if (isSubmitted) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl ${isCompact ? 'p-6' : 'p-8'} animate-scale-in`} data-testid="quote-form-success">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="font-heading font-semibold text-xl text-brand-navy mb-2">Thank You!</h3>
          <p className="text-brand-charcoal font-body">
            Your quote request has been submitted. We'll contact you within 24 hours.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl shadow-xl ${isCompact ? 'p-6' : 'p-8'}`} data-testid="quote-form" id="quote-form">
      <div className="mb-6">
        <p className="text-brand-accent font-body text-sm font-semibold uppercase tracking-widest mb-2">
          Free Estimate
        </p>
        <h3 className="font-heading font-bold text-2xl text-brand-navy">
          Get Your Quote
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name and Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="font-body text-sm text-brand-charcoal">
              Full Name *
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Smith"
              className="form-input-focus font-body"
              data-testid="quote-form-name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="font-body text-sm text-brand-charcoal">
              Email Address *
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@example.com"
              className="form-input-focus font-body"
              data-testid="quote-form-email"
              required
            />
          </div>
        </div>

        {/* Phone and Service Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="phone" className="font-body text-sm text-brand-charcoal">
              Phone Number *
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(512) 555-0123"
              className="form-input-focus font-body"
              data-testid="quote-form-phone"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="service_type" className="font-body text-sm text-brand-charcoal">
              Service Type *
            </Label>
            <Select 
              value={formData.service_type} 
              onValueChange={(value) => handleSelectChange('service_type', value)}
            >
              <SelectTrigger className="font-body" data-testid="quote-form-service-type">
                <SelectValue placeholder="Select service type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="residential">Residential Cleaning</SelectItem>
                <SelectItem value="construction">Post-Construction Cleaning</SelectItem>
                <SelectItem value="commercial">Commercial Cleaning</SelectItem>
                <SelectItem value="deep-clean">Deep Cleaning</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Property Type and Requested Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="property_type" className="font-body text-sm text-brand-charcoal">
              Property Type
            </Label>
            <Select 
              value={formData.property_type} 
              onValueChange={(value) => handleSelectChange('property_type', value)}
            >
              <SelectTrigger className="font-body" data-testid="quote-form-property-type">
                <SelectValue placeholder="Select property type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="house">Single Family Home</SelectItem>
                <SelectItem value="apartment">Apartment/Condo</SelectItem>
                <SelectItem value="townhouse">Townhouse</SelectItem>
                <SelectItem value="luxury-estate">Luxury Estate</SelectItem>
                <SelectItem value="hotel">Hotel</SelectItem>
                <SelectItem value="apartment-complex">Apartment Complex</SelectItem>
                <SelectItem value="office">Office Building</SelectItem>
                <SelectItem value="construction-site">Construction Site</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="requested_date" className="font-body text-sm text-brand-charcoal">
              Preferred Service Date
            </Label>
            <Input
              id="requested_date"
              name="requested_date"
              type="date"
              value={formData.requested_date}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              className="form-input-focus font-body"
              data-testid="quote-form-date"
            />
          </div>
        </div>

        {/* Property Address */}
        <div className="space-y-2">
          <Label htmlFor="property_address" className="font-body text-sm text-brand-charcoal">
            Property Address *
          </Label>
          <Input
            id="property_address"
            name="property_address"
            type="text"
            value={formData.property_address}
            onChange={handleChange}
            placeholder="Enter the full property address"
            className="form-input-focus font-body"
            data-testid="quote-form-address"
            required
          />
        </div>

        {/* Request Details */}
        <div className="space-y-2">
          <Label htmlFor="request_details" className="font-body text-sm text-brand-charcoal">
            Request Details *
          </Label>
          <Textarea
            id="request_details"
            name="request_details"
            value={formData.request_details}
            onChange={handleChange}
            placeholder="Please describe your cleaning needs in detail: property size, number of rooms, specific areas to focus on, any special requirements..."
            rows={4}
            className="form-input-focus font-body resize-none"
            data-testid="quote-form-details"
          />
        </div>

        {/* Additional Message */}
        <div className="space-y-2">
          <Label htmlFor="message" className="font-body text-sm text-brand-charcoal">
            Additional Notes (Optional)
          </Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Any additional information, access instructions, or special requests..."
            rows={2}
            className="form-input-focus font-body resize-none"
            data-testid="quote-form-message"
          />
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-brand-navy hover:bg-brand-action text-white font-body font-medium rounded-full py-6 btn-shine"
          data-testid="quote-form-submit"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Request Free Quote
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

export default QuoteForm;
